import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import { RosterRef } from '../../invitations/models/invitation.model';
import { OrgMockService } from '../../../service/org-mock.service';
import { ReportDraft, ReportType } from '../models/report.model';
import { ReportMockService } from '../services/report-mock.service';

@Component({
  selector: 'app-report-wizard',
  templateUrl: './report-wizard.component.html',
  styleUrls: ['./report-wizard.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ReportWizardComponent implements OnInit {
  currentStep = 1;
  readonly lastStep = 3;
  orgs: RosterRef[] = [];
  form!: FormGroup;
  editId: string | null = null;
  loading = true;

  constructor(
    private fb: FormBuilder,
    private svc: ReportMockService,
    private orgSvc: OrgMockService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.orgs = this.orgSvc.listSync();
    this.form = this.fb.group({
      title: ['', Validators.required],
      ownerOrgId: ['', Validators.required],
      type: ['group_profile' as ReportType, Validators.required],
      // Not rendered as inputs — Step 2 owns them. They live on the form so the wizard has one
      // source of truth to save from.
      sourceOrgIds: [[] as string[]],
      participantIds: [[] as string[], Validators.required],
    });

    this.editId = this.route.snapshot.paramMap.get('id');
    if (!this.editId) { this.loading = false; return; }

    this.svc.getById(this.editId).subscribe(r => {
      if (!r) { this.router.navigate(['/reports']); return; }
      this.form.patchValue({
        title: r.title, ownerOrgId: r.ownerOrgId, type: r.type,
        sourceOrgIds: [...r.sourceOrgIds], participantIds: [...r.participantIds],
      });
      this.loading = false;
    });
  }

  get isEdit(): boolean { return !!this.editId; }
  get pageTitle(): string { return this.isEdit ? 'Edit Report' : 'New Report'; }

  private detailsValid(): boolean {
    return ['title', 'ownerOrgId', 'type'].every(c => this.form.get(c)!.valid);
  }
  private participantsValid(): boolean { return (this.form.value.participantIds as string[]).length > 0; }

  /** Why a step won't advance, rendered inline. Empty means it will. */
  blockReason(): string {
    if (this.currentStep === 1 && !this.detailsValid()) { return 'Complete the report details to continue.'; }
    if (this.currentStep === 2 && !this.participantsValid()) { return 'Add at least one participant to continue.'; }
    return '';
  }

  next(): void { this.goToStep(this.currentStep + 1); }
  back(): void { this.currentStep = Math.max(1, this.currentStep - 1); }

  /** Jump to a step; moving forward validates each step on the way, as the invitation wizard does. */
  goToStep(target: number): void {
    target = Math.max(1, Math.min(this.lastStep, target));
    if (target > this.currentStep) {
      for (let s = this.currentStep; s < target; s++) {
        if (s === 1 && !this.detailsValid()) {
          this.currentStep = 1;
          ['title', 'ownerOrgId', 'type'].forEach(c => this.form.get(c)!.markAsTouched());
          return;
        }
        if (s === 2 && !this.participantsValid()) { this.currentStep = 2; return; }
      }
    }
    this.currentStep = target;
  }

  cancel(): void { this.router.navigate(['/reports']); }

  private buildDraft(): ReportDraft {
    const v = this.form.value;
    return {
      title: v.title, ownerOrgId: v.ownerOrgId, type: v.type,
      sourceOrgIds: v.sourceOrgIds, participantIds: v.participantIds,
    };
  }

  /**
   * Generate is the only exit — there is no "save without generating". With Last Run dropped there
   * is no saved-but-never-generated state for the listing to show, so a second button would create
   * a distinction nothing can render.
   */
  generate(): void {
    if (!this.detailsValid()) { this.goToStep(1); return; }
    if (!this.participantsValid()) { this.goToStep(2); return; }

    const done = () => {
      Swal.fire({
        title: this.isEdit ? 'Report saved' : 'Report created',
        text: 'Report generation will run once Reports are connected to live data.',
        icon: 'info', confirmButtonText: 'Got it',
      }).then(() => this.router.navigate(['/reports']));
    };

    const draft = this.buildDraft();
    // Prune participantIds down to who is ACTUALLY still in the live pool for the chosen orgs,
    // right before saving. A participant can go stale between selection and save — their
    // invitation gets archived, or their profile gets locked — and they then vanish from the
    // picker's pool (nothing to un-toggle) while surviving untouched in the form value. generate()
    // is the one choke point every save path funnels through, including Step 1 -> Step 3 via the
    // stepper, which never mounts the picker and so never gets a chance to prune. Filtering
    // (rather than rebuilding from the pool) preserves the user's existing ordering.
    this.svc.pool(draft.sourceOrgIds).subscribe(pool => {
      const liveIds = new Set(pool.map(row => row.participant.id));
      const pruned = { ...draft, participantIds: draft.participantIds.filter(id => liveIds.has(id)) };
      if (this.editId) { this.svc.update(this.editId, pruned).subscribe(done); }
      else { this.svc.create(pruned).subscribe(done); }
    });
  }
}
